<template>
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h3 class="text-lg font-medium text-gray-900 mb-4">Liste de fiches du Lookbook</h3>
    
    <!-- Instructions -->
    <div class="mb-6 space-y-2 text-sm text-gray-600">
      <p>- Pour un affichage optimal de vos lookbook, la dimension de la photo ne doit pas dépasser 1600px en hauteur et en largeur, doit être inférieur à 2Mo et au format "JPG"</p>
      <p>- Si la taille est inférieure à 1600px la photo se redimensionnera automatiquement</p>
      <p>- L'icone <i class="fas fa-image"></i> sur chacune des photos de lookbook permet de désigner une image de couverture pour l'affichage sur le catalogue d'Efashion Paris.</p>
    </div>
    
    <!-- Upload de nouvelle fiche -->
    <div class="mb-6">
      <div class="flex items-center gap-4">
        <input 
          ref="fileInput"
          type="file" 
          accept="image/jpeg"
          @change="handleFileChange"
          class="hidden"
        />
        <button 
          type="button"
          @click="fileInput?.click()"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Choisir un fichier
        </button>
        <span class="text-sm text-gray-500">
          {{ selectedFile ? selectedFile.name : 'Aucun fichier choisi' }}
        </span>
        <button 
          type="button"
          @click="$emit('add-fiche', selectedFile)"
          :disabled="!selectedFile || isUploading"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="isUploading" class="fas fa-spinner fa-spin mr-2"></i>
          <i v-else class="fas fa-plus mr-2"></i>
          Ajouter une fiche
        </button>
      </div>
    </div>
    
    <!-- Liste des fiches existantes -->
    <div v-if="fiches.length > 0" class="space-y-4">
      <h4 class="font-medium text-gray-900">Liste des fiches du lookbook :</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          v-for="fiche in fiches" 
          :key="fiche.id"
          class="relative border border-gray-200 rounded-lg overflow-hidden"
        >
          <!-- Image de la fiche -->
          <div class="aspect-video bg-gray-200 flex items-center justify-center">
            <div v-if="!fiche.image_url" class="text-center text-gray-500">
              <i class="fas fa-image text-2xl mb-2"></i>
              <div class="text-sm">Aucune image</div>
            </div>
            <img 
              v-else 
              :src="fiche.image_url" 
              :alt="`Fiche ${fiche.ordre}`"
              class="w-full h-full object-cover"
            />
            
            <!-- Badge de couverture -->
            <div v-if="fiche.couverture" class="absolute top-2 right-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <i class="fas fa-image mr-1"></i>
                Couverture
              </span>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="p-3 bg-gray-50">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-gray-600">ID: {{ fiche.id }}</span>
              <div class="flex items-center gap-1">
                <!-- Bouton Éditer -->
                <button 
                  type="button"
                  @click="$emit('edit-fiche', fiche)"
                  class="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="Éditer"
                >
                  <i class="fas fa-edit text-sm"></i>
                </button>
                
                <!-- Bouton Supprimer -->
                <button 
                  type="button"
                  @click="$emit('delete-fiche', fiche)"
                  class="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Supprimer"
                >
                  <i class="fas fa-trash text-sm"></i>
                </button>
                
                <!-- Bouton Couverture -->
                <button 
                  type="button"
                  @click="$emit('set-couverture', fiche)"
                  :class="[
                    'p-1 rounded',
                    fiche.couverture 
                      ? 'text-green-600 bg-green-100' 
                      : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                  ]"
                  :title="fiche.couverture ? 'Couverture' : 'Définir comme couverture'"
                >
                  <i class="fas fa-image text-sm"></i>
                </button>
              </div>
            </div>
            
            <!-- Contrôles d'ordre -->
            <div class="flex items-center justify-center gap-2">
              <button 
                type="button"
                @click="$emit('change-ordre', fiche, 'prev')"
                :disabled="fiche.ordre === 1"
                class="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Déplacer vers la gauche"
              >
                <i class="fas fa-chevron-left text-sm"></i>
              </button>
              <span class="text-xs text-gray-500">{{ fiche.ordre }}</span>
              <button 
                type="button"
                @click="$emit('change-ordre', fiche, 'next')"
                :disabled="fiche.ordre === fiches.length"
                class="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Déplacer vers la droite"
              >
                <i class="fas fa-chevron-right text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Message si aucune fiche -->
    <div v-else class="text-center py-8 text-gray-500">
      <i class="fas fa-book-open text-3xl mb-2"></i>
      <div>Aucune fiche n'existe pour ce lookbook</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { LookbookFicheDisplay } from '@/types/lookbook-fiche'

interface Props {
  fiches: LookbookFicheDisplay[]
  isUploading?: boolean
}

interface Emits {
  (e: 'add-fiche', file: File | undefined): void
  (e: 'edit-fiche', fiche: LookbookFicheDisplay): void
  (e: 'delete-fiche', fiche: LookbookFicheDisplay): void
  (e: 'set-couverture', fiche: LookbookFicheDisplay): void
  (e: 'change-ordre', fiche: LookbookFicheDisplay, direction: 'prev' | 'next'): void
}

const props = withDefaults(defineProps<Props>(), {
  isUploading: false
})

defineEmits<Emits>()

const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File>()

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (file) {
    // Vérifier le format JPG
    if (!file.type.includes('image/jpeg')) {
      alert('Seuls les fichiers JPG sont acceptés')
      target.value = ''
      return
    }
    
    // Vérifier la taille (2 Mo max)
    const maxSize = 2 * 1024 * 1024 // 2 Mo
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. Taille maximale : 2 Mo')
      target.value = ''
      return
    }
    
    selectedFile.value = file
  }
}

// Réinitialiser le fichier après upload réussi
watch(() => props.isUploading, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    selectedFile.value = undefined
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
})
</script>

